import * as React from 'react'
import { cn } from '../../lib/utils'
import { inputContainerVariants } from '../input/input-container'
import { Icon } from '../icon'
import { CheckIcon, MinusIcon } from 'lucide-react'

export interface MultiSelectOption {
  value: string
  label: string
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  disabled?: boolean
  inputSize?: 'sm' | 'md' | 'lg'
  variant?: 'fill' | 'line'
  className?: string
  searchPlaceholder?: string
  emptyMessage?: string
  renderSelectedLabel?: (selected: MultiSelectOption[]) => React.ReactNode
  // i18n props
  selectAllLabel?: string
  deselectAllLabel?: string
  selectedCountLabel?: (count: number) => string
}

// Simple checkbox indicator without Radix to avoid ref issues
function CheckIndicator({
  checked,
  indeterminate,
}: {
  checked: boolean
  indeterminate?: boolean
}) {
  return (
    <div
      className={cn(
        'size-5 rounded border-[1.5px] flex items-center justify-center shrink-0',
        checked || indeterminate
          ? 'bg-base-main border-base-main text-white'
          : 'bg-base-surface-5 border-base-surface-20',
      )}
    >
      {indeterminate ? (
        <MinusIcon className='size-3.5' strokeWidth={3} />
      ) : checked ? (
        <CheckIcon className='size-3.5' strokeWidth={3} />
      ) : null}
    </div>
  )
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select options',
  disabled = false,
  inputSize = 'md',
  variant = 'fill',
  className,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No options found',
  renderSelectedLabel,
  selectAllLabel = 'Select all',
  deselectAllLabel = 'Deselect all',
  selectedCountLabel = (count) => `${count} selected`,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState('')
  const containerRef = React.useRef<HTMLDivElement>(null)
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  const filteredOptions = React.useMemo(
    () =>
      options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [options, searchTerm],
  )

  const selectedOptions = React.useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [options, value],
  )

  const handleToggle = React.useCallback(
    (optionValue: string) => {
      if (value.includes(optionValue)) {
        onChange(value.filter((v) => v !== optionValue))
      } else {
        onChange([...value, optionValue])
      }
    },
    [value, onChange],
  )

  const handleSelectAll = React.useCallback(() => {
    if (value.length === options.length) {
      onChange([])
    } else {
      onChange(options.map((o) => o.value))
    }
  }, [value, options, onChange])

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when dropdown opens
  React.useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  const displayValue = React.useMemo(() => {
    if (selectedOptions.length === 0) {
      return <span className='text-neutral-surface-60'>{placeholder}</span>
    }

    if (renderSelectedLabel) {
      return renderSelectedLabel(selectedOptions)
    }

    return (
      <span className='truncate'>
        {selectedOptions.map((o) => o.label).join('، ')}
      </span>
    )
  }, [selectedOptions, placeholder, renderSelectedLabel])

  return (
    <div ref={containerRef} className='relative w-full'>
      <button
        type='button'
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          'items-center justify-between whitespace-nowrap w-full text-start',
          inputContainerVariants({
            inputSize,
            variant,
          }),
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        <span className='flex-1 truncate'>{displayValue}</span>
        <Icon
          name='TriangleDown'
          className={cn(
            'size-icon-sm transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && (
        <div className='absolute z-50 mt-1 w-full min-w-[8rem] bg-neutral-surface border rounded-md shadow-lg overflow-hidden'>
          {/* Search Input */}
          <div className='p-2 border-b'>
            <div className='relative'>
              <Icon
                name='SearchLine'
                className='absolute start-2 top-1/2 -translate-y-1/2 size-icon-sm text-neutral-surface-60'
              />
              <input
                ref={searchInputRef}
                type='text'
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full ps-8 pe-2 py-1.5 text-sm bg-neutral-surface-5 border border-neutral-surface-20 rounded focus:outline-none focus:border-base-main'
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Select All Option */}
          {filteredOptions.length > 1 && (
            <div
              className='flex items-center gap-2 px-3 py-2 hover:bg-neutral-surface-10 cursor-pointer border-b'
              onClick={handleSelectAll}
            >
              <CheckIndicator
                checked={value.length === options.length}
                indeterminate={
                  value.length > 0 && value.length < options.length
                }
              />
              <span className='text-sm font-medium'>
                {value.length === options.length
                  ? deselectAllLabel
                  : selectAllLabel}
              </span>
            </div>
          )}

          {/* Options List */}
          <div className='max-h-60 overflow-y-auto'>
            {filteredOptions.length === 0 ? (
              <div className='p-3 text-sm text-neutral-surface-60 text-center'>
                {emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className='flex items-center gap-2 px-3 py-2 hover:bg-neutral-surface-10 cursor-pointer'
                  onClick={() => handleToggle(option.value)}
                >
                  <CheckIndicator checked={value.includes(option.value)} />
                  <bdi className='text-sm'>{option.label}</bdi>
                </div>
              ))
            )}
          </div>

          {/* Selected Count Footer */}
          {selectedOptions.length > 0 && (
            <div className='px-3 py-2 border-t bg-neutral-surface-5 text-xs text-neutral-surface-60'>
              {selectedCountLabel(selectedOptions.length)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
